// Lap enrichment — match lap timestamps or distance bounds to samples.

import { computePowerMetrics } from "./powerMetrics";
import { hasValidDistance } from "./chartAxis";
import { distanceValue, speedValue } from "./format";

function durationFromSamples(slice) {
  const t0 = slice[0]?.t ? new Date(slice[0].t).getTime() : NaN;
  const t1 = slice[slice.length - 1]?.t ? new Date(slice[slice.length - 1].t).getTime() : NaN;
  if (Number.isFinite(t0) && Number.isFinite(t1) && t1 >= t0) {
    return Math.round((t1 - t0) / 1000);
  }
  return null;
}

function findIdxByDistance(samples, targetDist, afterIdx = 0) {
  let best = afterIdx;
  let bestDiff = Infinity;
  for (let i = afterIdx; i < samples.length; i++) {
    const d = samples[i].distance_m ?? 0;
    const diff = Math.abs(d - targetDist);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return best;
}

export function enrichLaps(laps, samples, ftp = null) {
  if (!laps?.length || !samples?.length) return [];

  const sorted = [...laps].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );

  const hasDistance = hasValidDistance(samples);

  return sorted.map((lap, i) => {
    let startIdx = 0;
    let endIdx = samples.length - 1;

    const startMs = lap.start_time ? new Date(lap.start_time).getTime() : NaN;
    const hasStartTime = Number.isFinite(startMs);

    // Prefer lap timestamps from the file — distance bounds are often wrong (cumulative / relative).
    if (hasStartTime) {
      const nextStartMs = sorted[i + 1]?.start_time
        ? new Date(sorted[i + 1].start_time).getTime()
        : Infinity;

      const foundStart = samples.findIndex((s) => s.t && new Date(s.t).getTime() >= startMs);
      startIdx = foundStart < 0 ? 0 : foundStart;

      const foundEnd = samples.findIndex((s) => s.t && new Date(s.t).getTime() >= nextStartMs);
      endIdx = foundEnd < 0 ? samples.length - 1 : Math.max(startIdx, foundEnd - 1);
    } else if (hasDistance && lap.start_distance_m != null && lap.end_distance_m != null) {
      startIdx = findIdxByDistance(samples, lap.start_distance_m);
      endIdx = findIdxByDistance(samples, lap.end_distance_m, startIdx);
    }

    if (endIdx < startIdx) {
      endIdx = startIdx;
    }

    const lapSamples = samples.slice(startIdx, endIdx + 1);

    const startDistance = lapSamples[0]?.distance_m ?? lap.start_distance_m ?? 0;
    const endDistance =
      lapSamples[lapSamples.length - 1]?.distance_m ??
      lap.end_distance_m ??
      startDistance + (lap.distance_m || 0);

    const alts = lapSamples.map((s) => s.altitude_m).filter((a) => a != null);
    const elevationM =
      alts.length >= 2 ? Math.round(alts[alts.length - 1] - alts[0]) : null;

    const computedDistM = Math.max(0, endDistance - startDistance);
    const distM =
      computedDistM > 0
        ? computedDistM
        : lap.distance_m > 0
          ? lap.distance_m
          : 0;

    const durationSeconds =
      lap.duration_seconds > 0
        ? lap.duration_seconds
        : durationFromSamples(lapSamples);

    const gradientPct =
      elevationM != null && distM > 0
        ? Math.round((elevationM / distM) * 100 * 10) / 10
        : null;

    const powers = lapSamples.map((s) => s.power).filter((p) => p != null);
    const avgPower =
      lap.avg_power ??
      (powers.length
        ? Math.round(powers.reduce((a, b) => a + b, 0) / powers.length)
        : null);

    const avgSpeedMs =
      lap.avg_speed_ms ??
      (distM > 0 && durationSeconds
        ? distM / durationSeconds
        : null);

    const hrs = lapSamples.map((s) => s.hr).filter((h) => h != null);
    const cadences = lapSamples.map((s) => s.cadence).filter((c) => c != null);
    const avgCadence =
      lap.avg_cadence ??
      (cadences.length
        ? Math.round(cadences.reduce((a, b) => a + b, 0) / cadences.length)
        : null);
    const avgHr =
      lap.avg_hr ??
      (hrs.length ? Math.round(hrs.reduce((a, b) => a + b, 0) / hrs.length) : null);

    const powerMetrics =
      ftp && powers.length
        ? computePowerMetrics(lapSamples, durationSeconds, ftp)
        : {
            normalized_power: null,
            intensity_factor: null,
            tss: null,
            efficiency_factor: null,
          };

    return {
      ...lap,
      lapNumber: i + 1,
      startIdx,
      endIdx,
      start_distance_m: startDistance,
      end_distance_m: endDistance,
      distance_m: distM,
      duration_seconds: durationSeconds,
      elevation_m: elevationM,
      gradient_pct: gradientPct,
      avg_power: avgPower,
      avg_speed_ms: avgSpeedMs,
      avg_hr: avgHr,
      avg_cadence: avgCadence,
      max_power: powers.length ? Math.max(...powers) : null,
      max_hr: hrs.length ? Math.max(...hrs) : lap.max_hr ?? null,
      normalized_power: powerMetrics.normalized_power,
      intensity_factor: powerMetrics.intensity_factor,
      tss: powerMetrics.tss,
      efficiency_factor: powerMetrics.efficiency_factor,
    };
  });
}

export function gpsPointsForLap(samples, lap) {
  if (lap.start_distance_m != null && lap.end_distance_m != null) {
    return samples
      .filter((s) => {
        const d = s.distance_m ?? 0;
        return (
          s.lat != null &&
          s.lon != null &&
          d >= lap.start_distance_m &&
          d <= lap.end_distance_m
        );
      })
      .map((s) => [s.lat, s.lon]);
  }

  return samples
    .slice(lap.startIdx, lap.endIdx + 1)
    .filter((s) => s.lat != null && s.lon != null)
    .map((s) => [s.lat, s.lon]);
}

/** Downsample chart points for a cleaner, smoother profile. */
export function downsampleChartData(data, maxPoints = 120) {
  if (!data?.length || data.length <= maxPoints) return data || [];
  const step = data.length / maxPoints;
  const result = [];
  for (let i = 0; i < maxPoints; i++) {
    result.push(data[Math.floor(i * step)]);
  }
  if (result[result.length - 1] !== data[data.length - 1]) {
    result.push(data[data.length - 1]);
  }
  return result;
}

/** Forward-fill brief null gaps so lines stay visible (display only). */
export function fillChartMetricGaps(rows, keys) {
  if (!rows?.length) return rows || [];
  const filled = rows.map((row) => ({ ...row }));

  for (const key of keys) {
    let last = null;
    for (let i = 0; i < filled.length; i++) {
      if (filled[i][key] != null && !Number.isNaN(filled[i][key])) {
        last = filled[i][key];
      } else if (last != null) {
        filled[i][key] = last;
      }
    }
    let next = null;
    for (let i = filled.length - 1; i >= 0; i--) {
      if (filled[i][key] != null && !Number.isNaN(filled[i][key])) {
        next = filled[i][key];
      } else if (next != null) {
        filled[i][key] = next;
      }
    }
  }

  return filled;
}

function lerpMetric(a, b, t) {
  if (a != null && b != null) return a + (b - a) * t;
  return a ?? b ?? null;
}

function interpolateAtKm(sorted, km, key) {
  if (!sorted.length) return null;
  if (km <= sorted[0].km) return sorted[0][key];
  if (km >= sorted[sorted.length - 1].km) return sorted[sorted.length - 1][key];

  for (let i = 0; i < sorted.length - 1; i++) {
    const left = sorted[i];
    const right = sorted[i + 1];
    if (km < left.km || km > right.km) continue;
    const span = right.km - left.km;
    if (span <= 0) return left[key] ?? right[key];
    const t = (km - left.km) / span;
    return lerpMetric(left[key], right[key], t);
  }
  return null;
}

/** Evenly space chart points across lap distance and interpolate metrics — fixes broken lines. */
export function resampleChartByDistance(rows, pointCount = 150) {
  if (!rows?.length) return rows || [];
  const sorted = [...rows].sort((a, b) => a.km - b.km);
  if (sorted.length < 2) return sorted;

  const minKm = sorted[0].km;
  const maxKm = sorted[sorted.length - 1].km;
  if (maxKm - minKm < 0.0001) return sorted;

  const n = Math.min(pointCount, Math.max(60, sorted.length));
  const out = [];
  for (let i = 0; i < n; i++) {
    const km = minKm + (i / (n - 1)) * (maxKm - minKm);
    out.push({
      km,
      hr: interpolateAtKm(sorted, km, "hr"),
      power: interpolateAtKm(sorted, km, "power"),
      speed_kmh: interpolateAtKm(sorted, km, "speed_kmh"),
      cadence: interpolateAtKm(sorted, km, "cadence"),
      elevation: interpolateAtKm(sorted, km, "elevation"),
      temperature: interpolateAtKm(sorted, km, "temperature"),
    });
  }
  return out;
}

/**
 * Build chart rows — full ride or a single lap (x-axis starts at 0 for lap view).
 * Prefer index slice (startIdx/endIdx) over distance filter — survives API downsampling.
 */
export function buildLapChartData(
  samples,
  hasDistance,
  lapSlice = null,
  unitSystem = "metric"
) {
  let scoped = samples;
  let startM = 0;

  if (lapSlice) {
    const { startIdx, endIdx, startM: sliceStartM, endM } = lapSlice;
    if (Number.isInteger(startIdx) && Number.isInteger(endIdx) && endIdx >= startIdx) {
      scoped = samples.slice(startIdx, endIdx + 1);
    } else if (hasDistance && sliceStartM != null && endM != null) {
      scoped = samples.filter((s) => {
        const d = s.distance_m ?? 0;
        return d >= sliceStartM && d <= endM;
      });
    }
    startM = scoped[0]?.distance_m ?? sliceStartM ?? 0;
  }

  const rows = scoped.map((s, i) => {
    let speed_kmh = s.speed_ms != null ? speedValue(s.speed_ms, unitSystem) : null;
    if (speed_kmh == null && i > 0 && s.t && scoped[i - 1].t && hasDistance) {
      const dt = (new Date(s.t).getTime() - new Date(scoped[i - 1].t).getTime()) / 1000;
      const dd = (s.distance_m || 0) - (scoped[i - 1].distance_m || 0);
      if (dt > 0 && dd >= 0) {
        speed_kmh = speedValue(dd / dt, unitSystem);
      }
    }
    return {
      km: hasDistance ? Math.max(0, distanceValue((s.distance_m || 0) - startM, unitSystem)) : i,
      hr: s.hr,
      power: s.power,
      speed_kmh,
      cadence: s.cadence,
      elevation: s.altitude_m,
      temperature: s.temperature_c,
    };
  });

  const filled = fillChartMetricGaps(rows, ["hr", "power", "speed_kmh", "cadence", "elevation", "temperature"]);

  if (lapSlice) {
    return resampleChartByDistance(filled);
  }

  return filled;
}
